/**
 * Authentication Service
 * Handles login, logout, token management, and authentication state
 */

import { API_ENDPOINTS, API_CONFIG, STORAGE_KEYS } from '../config/api.config';
import { currentUserService } from '../services/currentUserService';
import { apiPut } from '../utils/apiClient';
import { fetchAndStoreModalities as fetchAndStoreModalitiesUtil } from '../utils/modalityUtils';
import { clearPatientViewLanguage, resetI18nToStaffDefault } from '../utils/patientViewLanguage';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface AuthUser {
  id: string;
  username: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  roles?: string[];
  tenantID?: string;
  email_verified?: boolean;
  scope?: string;
  realm_access?: {
    roles?: string[];
  };
  resource_access?: {
    [key: string]: {
      roles?: string[];
    };
  };
  exp?: number;
  iat?: number;
  jti?: string;
  iss?: string;
  aud?: string | string[];
  typ?: string;
  azp?: string;
  sid?: string;
  acr?: string;
  'allowed-origins'?: string[];
  facility_id?: string;
  modality?: string[];
  facility?: {
    id: number;
    facility_name: string;
    facility_type: string;
    facility_code: string;
    has_radiologist_hierarchy?: boolean;
  };
}

export interface AuthResponse {
  success: boolean;
  user?: AuthUser;
  error?: string;
  message?: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  token_type: string;
  scope: string;
}

class AuthService {
  private static readonly AUTH_KEYS = [
    STORAGE_KEYS.ACCESS_TOKEN,
    STORAGE_KEYS.REFRESH_TOKEN,
    STORAGE_KEYS.TOKEN_EXPIRES_AT,
    STORAGE_KEYS.USER_DATA,
  ] as const;

  private static readonly RIS_EXTRA_KEYS = [
    STORAGE_KEYS.FACILITY_ID,
    STORAGE_KEYS.MODALITY,
    STORAGE_KEYS.FACILITY,
    STORAGE_KEYS.HAS_RADIOLOGIST_HIERARCHY,
    STORAGE_KEYS.MODALITIES,
  ] as const;

  private static readonly PATIENT_EXTRA_KEYS = [
    STORAGE_KEYS.IS_PATIENT_MODE,
    STORAGE_KEYS.PATIENT_DICOM_ID,
    STORAGE_KEYS.PATIENT_AGE,
    STORAGE_KEYS.PATIENT_GENDER,
  ] as const;

  private clearStorageKeys(storage: Storage, keys: readonly string[]): void {
    keys.forEach(key => storage.removeItem(key));
  }

  private isTokenValidInStorage(storage: Storage): boolean {
    const token = storage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) {
      return false;
    }

    const expiresAt = storage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    if (expiresAt) {
      const expiryTime = parseInt(expiresAt, 10);
      if (Date.now() >= expiryTime) {
        return false;
      }
    }

    return true;
  }

  /**
   * Patient sessions are stored in sessionStorage so they do not conflict with
   * RIS sessions in localStorage (shared across tabs).
   */
  isPatientSessionActive(): boolean {
    return this.isTokenValidInStorage(sessionStorage);
  }

  /**
   * RIS staff sessions are stored in localStorage.
   * Legacy patient sessions stored in localStorage are excluded.
   */
  isRisAuthenticated(): boolean {
    if (
      localStorage.getItem(STORAGE_KEYS.IS_PATIENT_MODE) === 'true' ||
      localStorage.getItem('is_patient_mode') === 'true'
    ) {
      return false;
    }
    return this.isTokenValidInStorage(localStorage);
  }

  /**
   * Check if the current tab has any active session (patient or RIS).
   */
  isAuthenticated(): boolean {
    return this.isPatientSessionActive() || this.isRisAuthenticated();
  }

  /**
   * Remove legacy patient auth data that was previously stored in localStorage.
   * Patient sessions now use sessionStorage to avoid conflicting with RIS sessions.
   */
  clearLegacyPatientAuthFromLocalStorage(): void {
    const legacyPatientMode =
      localStorage.getItem(STORAGE_KEYS.IS_PATIENT_MODE) === 'true' ||
      localStorage.getItem('is_patient_mode') === 'true';

    if (!legacyPatientMode) {
      return;
    }

    this.clearStorageKeys(localStorage, AuthService.AUTH_KEYS);
    localStorage.removeItem(STORAGE_KEYS.IS_PATIENT_MODE);
    localStorage.removeItem('is_patient_mode');
    localStorage.removeItem(STORAGE_KEYS.PATIENT_DICOM_ID);
    localStorage.removeItem('patient_dicom_id');
    localStorage.removeItem(STORAGE_KEYS.PATIENT_AGE);
    localStorage.removeItem('patient_age');
    localStorage.removeItem(STORAGE_KEYS.PATIENT_GENDER);
    localStorage.removeItem('patient_gender');
  }
  private extractTokenData(responseData: any): TokenResponse | null {
    if (responseData.data && responseData.data.access_token) {
      return {
        access_token: responseData.data.access_token,
        refresh_token: responseData.data.refresh_token || '',
        expires_in: responseData.data.expires_in || 3600,
        refresh_expires_in: responseData.data.refresh_expires_in || 1800,
        token_type: responseData.data.token_type || 'Bearer',
        scope: responseData.data.scope || '',
      };
    }
    if (responseData.access_token) {
      return {
        access_token: responseData.access_token,
        refresh_token: responseData.refresh_token || '',
        expires_in: responseData.expires_in || 3600,
        refresh_expires_in: responseData.refresh_expires_in || 1800,
        token_type: responseData.token_type || 'Bearer',
        scope: responseData.scope || '',
      };
    }
    return null;
  }

  private applyFacilityFromLoginResponse(user: AuthUser, responseData: any): void {
    if (!responseData.data) {
      return;
    }
    if (responseData.data.facility_id) {
      user.facility_id = responseData.data.facility_id;
      localStorage.setItem(STORAGE_KEYS.FACILITY_ID, responseData.data.facility_id);
    }
    if (responseData.data.facility) {
      user.facility = responseData.data.facility;
      localStorage.setItem(STORAGE_KEYS.FACILITY, JSON.stringify(user.facility));
      if (user.facility.has_radiologist_hierarchy !== undefined) {
        localStorage.setItem(
          STORAGE_KEYS.HAS_RADIOLOGIST_HIERARCHY,
          String(user.facility.has_radiologist_hierarchy)
        );
      }
    }
  }

  private completeLoginWithUser(user: AuthUser): AuthResponse {
    this.setUser(user);
    fetchAndStoreModalitiesUtil().catch(err => {
      console.error('Failed to fetch modalities:', err);
    });
    return { success: true, user };
  }

  private buildUserFromDirectResponse(responseData: any): AuthUser {
    return {
      id: responseData.id || responseData.user?.id || responseData.username,
      username: responseData.username || responseData.user?.username,
      email: responseData.email || responseData.user?.email,
      name: responseData.name || responseData.user?.name,
      given_name: responseData.given_name || responseData.user?.given_name,
      family_name: responseData.family_name || responseData.user?.family_name,
      roles: responseData.roles || responseData.user?.roles || [],
    };
  }

  private storeTokensFromDirectResponse(responseData: any): void {
    if (!responseData.token && !responseData.access_token) {
      return;
    }
    const tokenData: TokenResponse = {
      access_token: responseData.token || responseData.access_token,
      refresh_token: responseData.refresh_token || '',
      expires_in: responseData.expires_in || 3600,
      refresh_expires_in: responseData.refresh_expires_in || 3600,
      token_type: responseData.token_type || 'Bearer',
      scope: responseData.scope || '',
    };
    this.setTokens(tokenData);
  }

  /**
   * Login with username and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // Clear any legacy patient session from localStorage before RIS login
      this.clearLegacyPatientAuthFromLocalStorage();
      // Fresh login must require a new OTP before report approval (tab-scoped sessionStorage)
      sessionStorage.removeItem(STORAGE_KEYS.APPROVAL_SESSION_VALID_UNTIL);
      localStorage.removeItem(STORAGE_KEYS.APPROVAL_SESSION_VALID_UNTIL);
      // Prepare JSON body for new login API
      const requestBody = {
        username: credentials.username,
        password: credentials.password,
      };

      const response = await fetch(API_ENDPOINTS.AUTH.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json, text/plain, */*',
          'tenant-id': API_CONFIG.TENANT_ID,
        },
        body: JSON.stringify(requestBody),
        mode: 'cors', // Explicitly set CORS mode
        //        credentials: 'include', // Include cookies for session management
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Invalid credentials');
      }

      const responseData = await response.json();
      const tokenData = this.extractTokenData(responseData);

      // If we have token data, decode and extract user info
      if (tokenData) {
        this.setTokens(tokenData);
        const user = this.getUserFromToken(tokenData.access_token);
        if (!user) {
          throw new Error('Failed to parse user information from token');
        }
        this.applyFacilityFromLoginResponse(user, responseData);
        return this.completeLoginWithUser(user);
      }

      // If the API returns user data directly (without tokens)
      if (responseData.user || responseData.id || responseData.username) {
        const user = this.buildUserFromDirectResponse(responseData);
        this.storeTokensFromDirectResponse(responseData);
        return this.completeLoginWithUser(user);
      }

      // Fallback: try to extract user info from response
      throw new Error('Unexpected response format from login API');
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed. Please try again.',
      };
    }
  }

  private applyTokenExpiryFromDecoded(tokenData: TokenResponse, token: string): void {
    try {
      const decodedUser = this.getUserFromToken(token);
      if (decodedUser && decodedUser.exp) {
        const expiresIn = decodedUser.exp - Math.floor(Date.now() / 1000);
        if (expiresIn > 0) {
          tokenData.expires_in = expiresIn;
        }
      }
    } catch (error) {
      console.warn('Could not decode token for expiry calculation:', error);
    }
  }

  private buildPatientUser(token: string, patientData: any): AuthUser {
    const user = this.getUserFromToken(token);

    if (!user || !user.id) {
      return {
        id: patientData.id?.toString() || patientData.username || '',
        username: patientData.username || '',
        email: patientData.email || '',
        name: patientData.name || patientData.username || '',
        given_name: patientData.given_name || '',
        family_name: patientData.family_name || '',
        roles: ['patient'],
      };
    }

    if (patientData.name) {
      user.name = patientData.name;
    }
    if (patientData.username) {
      user.username = patientData.username;
    }
    if (patientData.email) {
      user.email = patientData.email;
    }
    if (!user.roles || !user.roles.includes('patient')) {
      user.roles = [...(user.roles || []), 'patient'];
    }
    return user;
  }

  private storePatientSessionData(patientData: any): void {
    if (patientData.patient_dicom_id) {
      sessionStorage.setItem(STORAGE_KEYS.PATIENT_DICOM_ID, patientData.patient_dicom_id);
    }
    if (patientData.age) {
      sessionStorage.setItem(STORAGE_KEYS.PATIENT_AGE, patientData.age);
    }
    if (patientData.gender) {
      sessionStorage.setItem(STORAGE_KEYS.PATIENT_GENDER, patientData.gender);
    }
    sessionStorage.setItem(STORAGE_KEYS.IS_PATIENT_MODE, 'true');
  }

  /**
   * Patient login with username and password
   * Uses a separate endpoint specifically for patient authentication
   */
  async patientLogin(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const requestBody = {
        username: credentials.username,
        password: credentials.password,
      };

      const response = await fetch(API_ENDPOINTS.AUTH.PATIENT_LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json, text/plain, */*',
        },
        body: JSON.stringify(requestBody),
        mode: 'cors',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Invalid credentials');
      }

      const responseData = await response.json();

      if (!responseData.success) {
        throw new Error(responseData.message || 'Login failed');
      }

      const token = responseData.token;
      if (!token) {
        throw new Error('Token not found in response');
      }

      const tokenData: TokenResponse = {
        access_token: token,
        refresh_token: '',
        expires_in: 3600,
        refresh_expires_in: 3600,
        token_type: 'Bearer',
        scope: '',
      };

      this.applyTokenExpiryFromDecoded(tokenData, token);
      this.setTokens(tokenData, sessionStorage);

      const patientData = responseData.data || {};
      const user = this.buildPatientUser(token, patientData);
      this.storePatientSessionData(patientData);
      this.setUser(user, sessionStorage);

      return {
        success: true,
        user,
      };
    } catch (error) {
      console.error('Patient login error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed. Please try again.',
      };
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = this.getRefreshToken();

      if (!refreshToken) {
        return false;
      }

      const formData = new URLSearchParams();
      formData.append('client_id', API_CONFIG.CLIENT_ID);
      formData.append('grant_type', API_CONFIG.REFRESH_GRANT_TYPE);
      formData.append('refresh_token', refreshToken);

      const response = await fetch(API_ENDPOINTS.AUTH.REFRESH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (!response.ok) {
        this.clearRisAuth();
        return false;
      }

      const tokenData: TokenResponse = await response.json();
      this.setTokens(tokenData);

      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearRisAuth();
      return false;
    }
  }

  /**
   * Logout user. Scope controls which session is cleared:
   * - 'ris': RIS staff session (localStorage)
   * - 'patient': patient session (sessionStorage)
   * - 'all': both sessions
   */
  async logout(options?: { scope?: 'ris' | 'patient' | 'all' }): Promise<void> {
    const scope = options?.scope ?? 'all';

    try {
      if (scope === 'ris' || scope === 'all') {
        const refreshToken = this.getRefreshToken();

        if (refreshToken) {
          const formData = new URLSearchParams();
          formData.append('client_id', API_CONFIG.CLIENT_ID);
          formData.append('refresh_token', refreshToken);

          await fetch(API_ENDPOINTS.AUTH.LOGOUT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString(),
          }).catch(() => {
            // Ignore logout endpoint errors
          });
        }
      }
    } finally {
      if (scope === 'ris' || scope === 'all') {
        this.clearRisAuth();
      }
      if (scope === 'patient' || scope === 'all') {
        this.clearPatientAuth();
      }
    }
  }

  /**
   * Clear all authentication data (both RIS and patient sessions).
   */
  clearAuth(): void {
    this.clearRisAuth();
    this.clearPatientAuth();
  }

  /**
   * Clear RIS staff authentication data from localStorage.
   */
  clearRisAuth(): void {
    this.clearStorageKeys(localStorage, AuthService.AUTH_KEYS);
    this.clearStorageKeys(localStorage, AuthService.RIS_EXTRA_KEYS);
    sessionStorage.removeItem(STORAGE_KEYS.APPROVAL_SESSION_VALID_UNTIL);
    localStorage.removeItem(STORAGE_KEYS.APPROVAL_SESSION_VALID_UNTIL);
    currentUserService.clearCurrentUser();
    window.dispatchEvent(new CustomEvent('userDataChanged', { detail: { user: null } }));
  }

  /**
   * Clear patient authentication data from sessionStorage.
   */
  clearPatientAuth(): void {
    this.clearStorageKeys(sessionStorage, AuthService.AUTH_KEYS);
    this.clearStorageKeys(sessionStorage, AuthService.PATIENT_EXTRA_KEYS);
    sessionStorage.removeItem(STORAGE_KEYS.IS_PATIENT_MODE);
    clearPatientViewLanguage();
    resetI18nToStaffDefault();
    window.dispatchEvent(new CustomEvent('userDataChanged', { detail: { user: null } }));
  }

  /**
   * Get current user from storage.
   * Patient session (sessionStorage) takes priority when active in the current tab.
   */
  getUser(): AuthUser | null {
    if (this.isPatientSessionActive()) {
      return this.readUserFromStorage(sessionStorage);
    }
    return this.readUserFromStorage(localStorage);
  }

  private restoreFacilityFields(user: AuthUser): void {
    if (!user.facility_id) {
      const facilityId = localStorage.getItem(STORAGE_KEYS.FACILITY_ID);
      if (facilityId) {
        user.facility_id = facilityId;
      }
    }

    if (!user.facility) {
      const facilityStr = localStorage.getItem(STORAGE_KEYS.FACILITY);
      if (facilityStr) {
        try {
          user.facility = JSON.parse(facilityStr);
        } catch {
          // Ignore parse errors
        }
      }
    }
  }

  private readUserFromStorage(storage: Storage): AuthUser | null {
    const userStr = storage.getItem(STORAGE_KEYS.USER_DATA);
    if (!userStr) {
      return null;
    }

    try {
      const user = JSON.parse(userStr) as AuthUser;

      // Restore facility_id and facility from localStorage for RIS users only
      if (storage === localStorage) {
        this.restoreFacilityFields(user);
      }

      return user;
    } catch {
      return null;
    }
  }

  /**
   * Get facility ID from storage
   */
  getFacilityId(): string | null {
    const user = this.getUser();
    if (user?.facility_id) {
      return user.facility_id;
    }
    return localStorage.getItem(STORAGE_KEYS.FACILITY_ID);
  }

  /**
   * Get facility from storage
   */
  getFacility(): AuthUser['facility'] | null {
    const user = this.getUser();
    if (user?.facility) {
      return user.facility;
    }
    const facilityStr = localStorage.getItem(STORAGE_KEYS.FACILITY);
    if (facilityStr) {
      try {
        return JSON.parse(facilityStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Get has_radiologist_hierarchy from storage
   */
  getHasRadiologistHierarchy(): boolean | null {
    const facility = this.getFacility();
    if (facility?.has_radiologist_hierarchy !== undefined) {
      return facility.has_radiologist_hierarchy;
    }
    return null;
  }

  /**
   * After a successful facility update, persist `has_radiologist_hierarchy` to localStorage
   * (`facility`, `user_data`, and the standalone key used by the report panel) so behavior
   * updates without a new login. Skips when the updated facility is not the signed-in user's facility.
   */
  syncStoredFacilityRadiologistHierarchy(updatedFacility: {
    id?: number | string;
    has_radiologist_hierarchy?: boolean;
  }): void {
    if (updatedFacility.has_radiologist_hierarchy === undefined) {
      return;
    }
    const storedFacilityId = this.getFacilityId();
    if (
      updatedFacility.id != null &&
      storedFacilityId != null &&
      String(updatedFacility.id) !== String(storedFacilityId)
    ) {
      return;
    }
    const currentUser = this.getUser();
    const currentFacility = currentUser?.facility || this.getFacility();
    if (!currentFacility) {
      return;
    }
    const updatedFacilityObj = {
      ...currentFacility,
      has_radiologist_hierarchy: updatedFacility.has_radiologist_hierarchy,
    };
    localStorage.setItem(STORAGE_KEYS.FACILITY, JSON.stringify(updatedFacilityObj));
    localStorage.setItem(
      STORAGE_KEYS.HAS_RADIOLOGIST_HIERARCHY,
      String(updatedFacility.has_radiologist_hierarchy)
    );
    if (currentUser) {
      const updatedUser = {
        ...currentUser,
        facility: updatedFacilityObj,
      };
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
      window.dispatchEvent(new CustomEvent('userDataChanged', { detail: { user: updatedUser } }));
    }
  }

  /**
   * Get access token from the active session storage.
   * RIS staff sessions take priority over patient sessionStorage when both could apply.
   */
  getAccessToken(): string | null {
    if (this.isRisAuthenticated()) {
      return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    }
    if (this.isPatientSessionActive()) {
      return sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    }
    return (
      localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
      sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    );
  }

  /**
   * Get refresh token (RIS sessions only).
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Check if token needs refresh (within 1 minute of expiry). RIS sessions only.
   */
  shouldRefreshToken(): boolean {
    const expiresAt = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    if (!expiresAt) {
      return false;
    }

    const expiryTime = parseInt(expiresAt, 10);
    const oneMinuteFromNow = Date.now() + 60000;

    return oneMinuteFromNow >= expiryTime;
  }

  /**
   * Store tokens in the given storage backend.
   */
  private setTokens(tokenData: TokenResponse, storage: Storage = localStorage): void {
    storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokenData.access_token);
    storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokenData.refresh_token);

    const expiresAt = Date.now() + tokenData.expires_in * 1000;
    storage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAt.toString());
  }

  /**
   * Store user data in the given storage backend.
   */
  private setUser(user: AuthUser, storage: Storage = localStorage): void {
    storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
    window.dispatchEvent(new CustomEvent('userDataChanged', { detail: { user } }));
  }

  /**
   * Decode JWT token and extract user info
   */
  private getUserFromToken(token: string): AuthUser | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const payload = JSON.parse(jsonPayload);

      // Extract roles from different possible locations in JWT
      let roles: string[] = [];

      // Check realm_access first
      if (payload.realm_access?.roles) {
        roles = [...roles, ...payload.realm_access.roles];
      }

      // Also check resource_access (don't use else if - we need both!)
      if (payload.resource_access) {
        // Check resource_access for roles
        const resourceAccess = Object.values(payload.resource_access) as Array<{
          roles?: string[];
        }>;
        resourceAccess.forEach(resource => {
          if (resource.roles) {
            roles = [...roles, ...resource.roles];
          }
        });
      }

      // Fallback to direct roles field
      if (payload.roles && roles.length === 0) {
        roles = Array.isArray(payload.roles) ? payload.roles : [payload.roles];
      }

      const user: AuthUser = {
        id: payload.sub || payload.id || payload.user_id || '',
        username: payload.preferred_username || payload.username || payload.sub || '',
        email: payload.email || '',
        name: payload.name || payload.preferred_username || '',
        given_name: payload.given_name || '',
        family_name: payload.family_name || '',
        roles: roles,
        tenantID: payload.tenantID || payload.tenant_id || undefined,
        email_verified: payload.email_verified !== undefined ? payload.email_verified : undefined,
        scope: payload.scope || undefined,
        realm_access: payload.realm_access || undefined,
        resource_access: payload.resource_access || undefined,
        exp: payload.exp || undefined,
        iat: payload.iat || undefined,
        jti: payload.jti || undefined,
        iss: payload.iss || undefined,
        aud: payload.aud || undefined,
        typ: payload.typ || undefined,
        azp: payload.azp || undefined,
        sid: payload.sid || undefined,
        acr: payload.acr || undefined,
      };

      // Handle 'allowed-origins' property with bracket notation
      if (payload['allowed-origins']) {
        (user as unknown as Record<string, unknown>)['allowed-origins'] =
          payload['allowed-origins'];
      }

      return user;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Get authorization header for API requests
   */
  getAuthHeader(): { Authorization: string } | Record<string, never> {
    const token = this.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Update password for a user (forget password functionality)
   */
  async updatePassword(
    newPassword: string,
    userId: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiPut<{ success?: boolean; message?: string; error?: string }>(
        API_ENDPOINTS.AUTH.UPDATE_PASSWORD,
        {
          newPassword,
          user_id: userId,
        }
      );

      if (response.data.success === false || response.status !== 200) {
        return {
          success: false,
          error: response.data.error || response.data.message || 'Failed to update password',
        };
      }

      return {
        success: true,
      };
    } catch (error) {
      console.error('Update password error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update password',
      };
    }
  }

  /**
   * Get current user data from /ris/api/auth/me endpoint
   */
  async getCurrentUser(): Promise<{
    success: boolean;
    data?: unknown;
    fromDatabase?: unknown;
    error?: string;
  }> {
    try {
      const { apiGet } = await import('../utils/apiClient');
      const response = await apiGet<{
        success: boolean;
        message?: string;
        data?: unknown;
        fromDatabase?: unknown;
      }>(API_ENDPOINTS.AUTH.GET_CURRENT_USER);

      if (response.data && typeof response.data === 'object') {
        const apiResponse = response.data as {
          success: boolean;
          message?: string;
          data?: unknown;
          fromDatabase?: unknown;
        };

        if (apiResponse.success) {
          return {
            success: true,
            data: apiResponse.data,
            fromDatabase: apiResponse.fromDatabase,
          };
        } else {
          return {
            success: false,
            error: apiResponse.message || 'Failed to fetch user data',
          };
        }
      }

      return {
        success: false,
        error: 'Invalid response format',
      };
    } catch (error) {
      console.error('Get current user error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch user data',
      };
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
